<template>
    <Dialog v-if="changelogs.length > 0" :visible="showDialog" modal @update:visible="handleClose"
        class="w-full md:w-3/4 lg:w-2/3 xl:w-1/2 max-h-[80vh]" :header="$t('changelog')">
        <div class="space-y-6 overflow-y-auto max-h-[60vh]">
            <div v-for="changelog in changelogs" :key="changelog.id?.toString()" class="changelog-item">
                <div class="markdown-content" v-html="renderMarkdown(changelog.content || '')"></div>
            </div>
        </div>
        <template #footer>
            <Button :label="$t('close')" @click="handleClose" />
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { useChangelogsStore } from "@/stores";
import { storeToRefs } from "pinia";
import { onMounted, computed } from "vue";

const { ensureChangelogsLoaded, markAsRead } = useChangelogsStore();
const { changelogs } = storeToRefs(useChangelogsStore());

const showDialog = computed(() => changelogs.value.length > 0);

onMounted(async () => {
    await ensureChangelogsLoaded();
});

const handleClose = () => {
    try {
        markAsRead();
    } catch (error) {
        console.error("Error marking changelogs as read:", error);
        // Handle error (e.g., show notification)
    } finally {
        changelogs.value.splice(0, changelogs.value.length);
    }

};

// Basic markdown renderer (supports headers, lists, bold, italic, code)
const renderMarkdown = (markdown: string): string => {
    let html = markdown
        // Headers
        .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
        // Bold
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Unordered lists
        .replace(/^\- (.+)$/gim, '<li class="ml-4">$1</li>')
        // Paragraphs (lines with text that don't start with special chars)
        .replace(/^(?!<[h|l|u]|$)(.+)$/gim, '<p class="mb-2">$1</p>');

    // Wrap consecutive list items in ul tags
    html = html.replace(/(<li.*?<\/li>\s*)+/g, '<ul class="list-disc mb-4">$&</ul>');

    return html;
};
</script>

<style scoped>
.markdown-content {
    color: rgb(31 41 55);
}

@media (prefers-color-scheme: dark) {
    .markdown-content {
        color: rgb(229 231 235);
    }
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3) {
    color: rgb(17 24 39);
}

@media (prefers-color-scheme: dark) {

    .markdown-content :deep(h1),
    .markdown-content :deep(h2),
    .markdown-content :deep(h3) {
        color: rgb(243 244 246);
    }
}

.markdown-content :deep(ul) {
    padding-left: 1rem;
}

.markdown-content :deep(li) {
    color: rgb(55 65 81);
}

@media (prefers-color-scheme: dark) {
    .markdown-content :deep(li) {
        color: rgb(209 213 219);
    }
}

.changelog-item:not(:last-child) {
    border-bottom: 1px solid rgb(229 231 235);
    padding-bottom: 1.5rem;
}

@media (prefers-color-scheme: dark) {
    .changelog-item:not(:last-child) {
        border-bottom-color: rgb(55 65 81);
    }
}
</style>